import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Avatar, Button} from 'antd';
import {PlusOutlined} from '@ant-design/icons';
import {
  StyledNavigationCompaniesEmpty,
  StyledNavigationCompanyDetails,
  StyledNavigationCompanySearch,
  StyledNavigationDropdownContent,
  StyledNavigationDropdownMenu,
  StyledNavigationCompaniesList
} from './StyledNavigation';
import Search from '../Search';
import {SettingsIcon} from '../../icons';
import {useEmployee, useIsAdmin, useIsEmployeeChanged, useUserCompanies} from '../../hooks';
import {userActions} from '../../state/actions/user.actions';
import routes from '../../routes/routes.json';
import {elementHelpers, objectHelpers, textHelpers} from '../../utils/helpers';

const DropdownNavigation = React.forwardRef(({
  enabledCompanyCreation = true,
  enabledCompanySearch = false,
  handleAddCompany,
  onCompanyChange,
  onMenuClick,
  open = false,
  ...rest
}, ref) => {
  const dispatch = useDispatch();
  const [t] = useTranslation('main');
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [companyQuery, setCompanyQuery] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const isAdmin = useIsAdmin();
  const employee = useEmployee();
  const allCompanies = useUserCompanies();

  const {activeCompany, companies} = useMemo(() => {
    let activeCompany;
    let userCompanies = [];
    if (employee.hasOwnProperty('company_id')) {
      userCompanies = allCompanies
        .filter(c => c.account_enabled)
        .filter(c => {
          if (c.company_id !== employee.company_id) {
            return c;
          } else {
            activeCompany = c;
            return false
          }
        });
    }
    return {activeCompany, companies: userCompanies};
  }, [allCompanies, employee]);

  const isEmployeeChanged = useIsEmployeeChanged();

  const isEnabledFooter = useMemo(() => enabledCompanyCreation || Boolean(companies.length), [enabledCompanyCreation, companies]);

  // Backoffice-support staff accumulate a company per client they onboard, so the list they scroll
  // is unbounded where an ordinary employee's is two or three. Everyone else keeps the plain list.
  const visibleCompanies = useMemo(() => {
    const query = companyQuery.trim().toLowerCase();
    if (!enabledCompanySearch || !query) return companies;
    // String(): a non-string name from a legacy record would throw here, and this renders inside
    // the navigation on every authenticated screen — one bad row must not take the whole app down.
    return companies.filter(c => String(objectHelpers.getObjProp(c, 'name') ?? '').toLowerCase().includes(query));
  }, [companies, companyQuery, enabledCompanySearch]);

  const navItems = [
    {key: 'settings', label: 'settings', path: routes.settings, icon: <SettingsIcon />},
  ]
    .map(i => ({...i, label: t(`bottomMenuToggle.navigation.${i.label}`)}));

  useEffect(() => {
    if (!open && companyQuery) setCompanyQuery('');
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isEmployeeChanged) {
      onCompanyChange && onCompanyChange();
      setSelectedKeys([]);
    }
  }, [isEmployeeChanged]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    elementHelpers.updateMenuItems({location, items: navItems, selectedKeys, setSelectedKeys});
  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMenuClick = (e) => {
    const {key} = e;
    const item = navItems.find(i => i.key === key);
    if (item) navigate(item.path);
    setSelectedKeys([key]);
    onMenuClick && onMenuClick();
  }

  const getCompanyDetails = ({company, key, ...rest}) => {
    const logo = objectHelpers.getObjProp(company, 'logo_url');
    const name = objectHelpers.getObjProp(company, 'name');
    return (
      <StyledNavigationCompanyDetails key={key} {...rest}>
        <Avatar
          src={logo}
          size={32}
        >
          {textHelpers.getInitials(name)}
        </Avatar>
        {name}
      </StyledNavigationCompanyDetails>
    )
  }

  const onCompanyClick = (id) => {
    onMenuClick && onMenuClick();
    // Leave the current page straight away: it would otherwise keep showing (or refetching into) the
    // previous company's state until the employee profile lands and `isEmployeeChanged` redirects.
    if (id !== activeCompany?.company_id) onCompanyChange && onCompanyChange();
    dispatch(userActions.changeCompany(
      id,
      (company) => {
        dispatch(userActions.disableEmployeeFromCompany(true, company));
        dispatch(userActions.getCompanies());
      }
    ));
  }

  return (
    <StyledNavigationDropdownContent ref={ref} {...rest}>
      <header>
        {getCompanyDetails({company: activeCompany})}
        {isAdmin && (
          <StyledNavigationDropdownMenu
            items={navItems}
            onClick={handleMenuClick}
            selectedKeys={selectedKeys}
          />
        )}
      </header>
      {isEnabledFooter && (
        <footer>
          {enabledCompanySearch && companies.length > 0 && (
            <StyledNavigationCompanySearch>
              <Search
                aria-label={t('searchCompanies')}
                onChange={(e) => setCompanyQuery(e.target.value)}
                placeholder={t('searchCompanies')}
                value={companyQuery}
              />
            </StyledNavigationCompanySearch>
          )}
          {visibleCompanies.length > 0 && (
            <StyledNavigationCompaniesList>
              {visibleCompanies.map(company =>
                getCompanyDetails({
                  company,
                  key: company.company_id,
                  onClick: () => onCompanyClick(company.company_id)
                }))}
            </StyledNavigationCompaniesList>
          )}
          {companies.length > 0 && visibleCompanies.length === 0 && (
            <StyledNavigationCompaniesEmpty role='status'>
              {t('emptySearchedResults')}
            </StyledNavigationCompaniesEmpty>
          )}
          {enabledCompanyCreation && (
            <Button
              onClick={(e) => handleAddCompany && handleAddCompany(e)}
              icon={<PlusOutlined/>}
              size='large'
            >
              {t('add')} {t('company')}
            </Button>
          )}
        </footer>
      )}
    </StyledNavigationDropdownContent>
  );
});

DropdownNavigation.propTypes = {
  enabledCompanyCreation: PropTypes.bool,
  enabledCompanySearch: PropTypes.bool,
  handleAddCompany: PropTypes.func,
  onCompanyChange: PropTypes.func,
  onMenuClick: PropTypes.func,
  open: PropTypes.bool
}

export default DropdownNavigation;
